import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Receipt,
  Loader2,
  ExternalLink,
  Download,
  ArrowLeft,
} from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { listMyStripeInvoices } from "@/lib/membership.functions";
import { formatMoney } from "@/lib/money";

export const Route = createFileRoute("/payment-history")({
  head: () => ({
    meta: [
      { title: "Payment History | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your Recovery Trauma™ membership payment history — past charges, invoices and receipts.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PaymentHistoryPage,
});


function formatDate(sec: number) {
  return new Date(sec * 1000).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function statusBadge(status: string | null) {
  const tone =
    status === "paid"
      ? "bg-emerald-100 text-emerald-900"
      : status === "open"
        ? "bg-amber-100 text-amber-900"
        : status === "void" || status === "uncollectible"
          ? "bg-rose-100 text-rose-900"
          : "bg-cream/80 text-foreground border border-gold/40";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${tone}`}
    >
      {status ?? "unknown"}
    </span>
  );
}

function PaymentHistoryPage() {
  const { isAuthed, loading: authLoading } = useMembership();
  const fetchInvoices = useServerFn(listMyStripeInvoices);

  const invoicesQuery = useQuery({
    queryKey: ["my-stripe-invoices"],
    queryFn: () => fetchInvoices({ data: undefined as never }),
    enabled: isAuthed,
  });

  if (authLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-2xl items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="font-serif text-3xl text-foreground">Payment History</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Please sign in to view your payment history.
        </p>
        <Link to="/login" className="btn-gold mt-6 text-sm">
          Sign in
        </Link>
      </div>
    );
  }

  const invoices = invoicesQuery.data ?? [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:py-12">
      <header className="mb-6">
        <Link
          to="/billing"
          className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Membership &amp; Billing
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--gradient-gold)]/20 text-[var(--gold-deep)]">
            <Receipt className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-serif text-2xl text-foreground md:text-3xl">
              Payment History
            </h1>
            <p className="text-sm text-muted-foreground">
              Past Recovery Trauma™ membership charges and invoices.
            </p>
          </div>
        </div>
      </header>

      {invoicesQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your payment history…
        </div>
      ) : invoicesQuery.isError ? (
        <div className="rounded-2xl border border-dashed border-rose-300 bg-rose-50/60 p-6 text-center text-sm text-rose-900">
          <p className="font-medium">We couldn't load your payment history.</p>
          <p className="mt-2">
            {(invoicesQuery.error as Error)?.message ?? "Please try again shortly."}
          </p>
        </div>
      ) : invoices.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-10 text-center text-sm text-muted-foreground">
          <p className="font-medium text-foreground">No payments yet.</p>
          <p className="mt-2">
            Once your Membership is active, your charges and invoices will appear here.
          </p>
          <Link to="/membership" className="btn-gold mt-5 text-sm">
            View Membership
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {invoices.map((inv) => (
            <li
              key={inv.id}
              className="rounded-2xl border border-gold/40 bg-card p-4 shadow-sm md:p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-lg text-foreground">
                      {formatMoney(inv.amount_paid || inv.amount_due, inv.currency, { fromCents: true })}
                    </span>
                    {statusBadge(inv.status)}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatDate(inv.created)}
                    {inv.number ? ` · Invoice ${inv.number}` : ""}
                  </p>
                  {inv.description ? (
                    <p className="mt-2 line-clamp-2 text-sm text-foreground/90">
                      {inv.description}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {inv.hosted_invoice_url ? (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-gold-outline text-xs"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> View
                    </a>
                  ) : null}
                  {inv.invoice_pdf ? (
                    <a
                      href={inv.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-gold-outline text-xs"
                    >
                      <Download className="h-3.5 w-3.5" /> PDF
                    </a>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-8 text-center text-xs text-muted-foreground">
        Need a refund or have a billing question?{" "}
        <Link to="/support" className="underline">
          Contact support
        </Link>
        .
      </p>
    </div>
  );
}
